import React, { useState, useEffect, useRef } from 'react';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import adminBlogService from '../../../services/admin/admin-blogServices';
import { useToast } from '../../../context/ToastContext';
import { UploadCloudIcon } from '../../../public/icons';

// Quill modules configuration
const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link', 'image'],
    ['clean']
  ],
};

const AddBlogModal = ({ isOpen, onClose, onSubmit, onSaveDraft, onUpdate, blogToEdit = null }) => {
  const { success, error } = useToast();
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    content: {
      intro: ''
    },
    featuredImage: null,
    scheduledDate: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  const categories = [
    'Memory Keeping', 
    'DIY & Craft', 
    'Stories', 
    'Travel', 
    'Culture', 
    'Food', 
    'Art', 
    'History', 
    'Lifestyle'
  ];

  const { quill, quillRef } = useQuill({ 
    theme: 'snow',
    modules,
    placeholder: 'Write your blog content here...',
    readOnly: isSubmitting || uploadingImage
  });

  // Handle editor content changes
  useEffect(() => {
    if (quill) {
      quill.on('text-change', () => {
        const content = quill.root.innerHTML;
        setFormData(prev => ({
          ...prev,
          content: {
            ...prev.content,
            intro: content
          }
        }));
      });
    }
  }, [quill]);

  // Load blog data when editing
  useEffect(() => {
    if (isOpen && quill) {
      if (blogToEdit) {
        setFormData({
          title: blogToEdit.title || '',
          category: blogToEdit.category || '',
          content: {
            intro: blogToEdit.content?.intro || ''
          },
          featuredImage: blogToEdit.featuredImage || null,
          scheduledDate: blogToEdit.scheduledDate ? new Date(blogToEdit.scheduledDate).toISOString().slice(0, 16) : ''
        });
        if (blogToEdit.featuredImage) {
          setPreviewImage(blogToEdit.featuredImage);
        }
        // Set editor content
        quill.root.innerHTML = blogToEdit.content?.intro || '';
      } else {
        setFormData({
          title: '',
          category: '',
          content: {
            intro: ''
          },
          featuredImage: null,
          scheduledDate: ''
        });
        setPreviewImage(null);
        quill.root.innerHTML = '';
      }
      setIsSubmitting(false);
      setUploadingImage(false);
    }
  }, [isOpen, blogToEdit, quill]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      error('Image size should be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      error('Please select an image file');
      return;
    }

    try {
      setUploadingImage(true);
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);

      const result = await adminBlogService.uploadImageToCloudinary(file);
      
      setFormData(prev => ({
        ...prev,
        featuredImage: result.url
      }));

      success('Image uploaded successfully!');
    } catch (uploadError) {
      console.error('Error uploading image:', uploadError);
      error('Failed to upload image. Please try again.');
      setPreviewImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    try {
      setIsSubmitting(true);

      if (!formData.title) {
        error('Please enter a blog title');
        return;
      }
      if (!formData.category) {
        error('Please select a category');
        return;
      }
      if (!formData.content.intro || formData.content.intro === '<p><br></p>') {
        error('Please enter content');
        return;
      }
      if (!formData.featuredImage) {
        error('Please upload a featured image');
        return;
      }

      // Strip HTML tags for excerpt
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formData.content.intro;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';

      const blogData = {
        title: formData.title,
        category: formData.category,
        excerpt: plainText.substring(0, 150),
        content: {
          intro: formData.content.intro,
          sections: []
        },
        tags: [],
        status: isDraft ? 'Draft' : (formData.scheduledDate ? 'Scheduled' : 'Published'),
        featuredImage: formData.featuredImage,
        metaTitle: formData.title,
        metaDescription: plainText.substring(0, 160),
        isFeatured: false
      };

      if (formData.scheduledDate) {
        blogData.scheduledDate = new Date(formData.scheduledDate).toISOString();
      }

      console.log('Submitting blog data:', blogData);

      if (blogToEdit) {
        await onUpdate(blogData);
      } else {
        if (isDraft && onSaveDraft) {
          await onSaveDraft(blogData);
        } else {
          await onSubmit(blogData);
        }
      }
      
      success(isDraft ? 'Draft saved successfully!' : 'Blog published successfully!');
      onClose();
    } catch (err) {
      console.error('Error submitting blog:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to submit blog';
      error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitEnabled = formData.title && 
                         formData.category &&
                         formData.content.intro && 
                         formData.content.intro !== '<p><br></p>' &&
                         formData.featuredImage && 
                         !isSubmitting && 
                         !uploadingImage;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity duration-300 text-left"
        onClick={onClose}
      />
      
      <div className="fixed right-0 top-0 h-full w-full md:w-[60%] text-left lg:w-[50%] xl:w-[40%] bg-[#F9F6F2] z-[999] overflow-y-auto transition-transform duration-300 ease-in-out">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-5 pt-4 border-b px-6 border-[#DBDBDB]">
            <div className="text-left">
              <h2 className="text-2xl font-bold text-[#1C2A46]">
                {blogToEdit ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h2>
              <p className="text-[#6B635D] text-sm font-light mt-1">
                {blogToEdit ? 'Update your blog post content.' : 'Share your thoughts and engage with your audience through compelling content.'}
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-[#F8F8F8] border border-[#DBDBDB] rounded text-2xl w-10 h-10 flex items-center justify-center"
              disabled={isSubmitting || uploadingImage}
            >
              ×
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {/* Blog Title */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Blog Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter an engaging blog title"
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] placeholder:text-sm placeholder:font-light focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                disabled={isSubmitting || uploadingImage}
              />
            </div>

            {/* Category Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full p-3 bg-[#F5F5F5] border-2 border-[#E4E4E4] focus:outline-none focus:ring-2 focus:ring-[#D2A146] focus:border-transparent"
                disabled={isSubmitting || uploadingImage}
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Featured Image */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Featured Image *
              </label>
              <div className="border-2 border-dashed border-[#DBDBDB] bg-[#F5F5F5] p-8 text-center hover:border-[#D2A146] transition-colors cursor-pointer rounded">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="featuredImage"
                  disabled={isSubmitting || uploadingImage}
                />
                <label htmlFor="featuredImage" className="cursor-pointer">
                  {uploadingImage ? (
                    <div className="space-y-4">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D2A146]"></div>
                      </div>
                      <p className="text-sm text-gray-600">Uploading image...</p>
                    </div>
                  ) : previewImage ? (
                    <div className="space-y-4">
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="w-full max-h-64 object-cover rounded mx-auto"
                      />
                      <p className="text-sm text-gray-600">Click to change image</p>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-center items-center mb-4">
                       <UploadCloudIcon/>
                      </div>
                      <p className="text-lg font-medium text-gray-700 mb-2">
                        Click to upload featured image
                      </p>
                      <p className="text-sm text-gray-500">Recommended size: 1200 × 800px. Used on category pages.</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Content Editor */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[#1C2A46] mb-2">
                Content Editor *
              </label>
              <div className="bg-white border-2 border-[#E4E4E4] rounded" style={{ height: '350px' }}>
                <div ref={quillRef} className="h-full" />
              </div>
              <p className="text-xs text-[#6B635D] mt-1">
                Use the toolbar to format your text. You can add headings, lists, links, and more.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-[#DBDBDB] flex gap-3 sticky bottom-0 bg-[#F9F6F2]">
            <button 
              onClick={() => handleSubmit(true)}
              disabled={isSubmitting || uploadingImage || !formData.featuredImage}
              className="px-6 py-3 border-2 border-[#DBDBDB] text-[#1C2A46] font-medium hover:bg-gray-50 transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save as Draft'}
            </button>
            <button 
              onClick={() => handleSubmit(false)}
              disabled={!isSubmitEnabled}
              className={`px-6 py-3 font-medium transition-colors flex-1 ${
                isSubmitEnabled
                  ? 'bg-[#D2A146] text-white hover:bg-[#C49660]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? 'Processing...' : (blogToEdit ? 'Update Blog' : (formData.scheduledDate ? 'Schedule Blog' : 'Publish Blog'))}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddBlogModal;